import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import PageWrapper from "../components/PageWrapper";
import { checkForUpdates } from "../utils/jsonUtils";
import { settings } from "../data/settings";
import { RDVButton } from "../components/commonWidgets";

import { TextField, IconButton, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import theme from "../theme";
import SEO from "../components/SEO";

// ----- Styled Components -----
const PageContainer = styled.div`
  color: #000;
  text-align: left;
  max-width: ${({ maxWidth }) => maxWidth || "100%"};
  margin: auto;
  width: 100%;
`;

/* Style du menu de catégories */
const CategoryMenuStyle2 = styled.div`
  margin: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  justify-content: center;
  border-bottom: 2px solid #eee;
  background-color: #f9f9f9;
`;

const ContentSection = styled.div`
  margin: 2rem;
`;

const CategoryButton = styled.button`
  background-color: #111;
  border: none;
  color: #fff;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 300;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-family: unset;
  text-transform: uppercase;

  h1 {
    padding: 0;
    font-size: unset;
    font-weight: unset;
    margin: 0;
  }

  &:hover {
    background-color: #fff;
    color: #111;
  }
`;

const CategorySection = styled.section`
  padding-top: 1rem;
  scroll-margin-top: 80px;
`;

const CategoryTitle = styled.h2`
  font-size: 1.8rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  ${theme.breakpoints.down("sm")} {
    font-size: 1.4rem;
  }
`;

const Description = styled.p`
  font-style: italic;
  margin-bottom: 0.5rem;
  color: #666;
`;

const ServiceCard = styled.div`
  border: 1px solid #ddd;
  padding: 12px 16px;
  margin-bottom: 10px;
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: column;

  @media (min-width: 600px) {
    flex-direction: row;
    align-items: center;
  }
`;

const ServiceName = styled.p`
  margin: 0;
  padding: 0;
  font-weight: bold;
  font-size: 1rem;
  color: #222;
`;

const ServiceInfos = styled.div`
  font-size: 0.9rem;
  color: #555;
  margin-top: 6px;
  white-space: nowrap;

  @media (min-width: 600px) {
    margin-top: 0;
  }
`;

const RdvButtonSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
`;

// ----- Main Component -----
const PagePrestations = ({ pageKey }) => {
  const [content, setContent] = useState(null);
  const [contentMap, setContentMap] = useState(null);
  const [search, setSearch] = useState("");
  const categoryRefs = useRef({});

  useEffect(() => {
    checkForUpdates(pageKey, setContent);
  }, [pageKey]);

  useEffect(() => {
    if (content) {
      const elementsById = content?.elements?.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
      setContentMap(elementsById);
    } else {
      setContentMap(null);
    }
  }, [content]);

  const scrollToCategory = (categoryId) => {
    const section = categoryRefs.current[categoryId];
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (!contentMap) return null;

  const structureElement = Object.values(contentMap).find(
    (el) => el.type === "serviceStructure"
  );

  if (!structureElement || !structureElement.categories) return null;

  const filteredCategories = structureElement.categories
    .map((cat) => {
      const filteredServices = cat.services.filter((service) => {
        const searchLower = search.toLowerCase();
        return (
          service.name.toLowerCase().includes(searchLower) ||
          (service.description &&
            service.description.toLowerCase().includes(searchLower)) ||
          cat.name.toLowerCase().includes(searchLower)
        );
      });

      if (filteredServices.length > 0) {
        return { ...cat, services: filteredServices };
      }

      return null;
    })
    .filter(Boolean);

  return (
    <PageWrapper style={{ backgroundColor: "#000", color: "#fff" }}>
      <SEO pageKey={pageKey} />
      <PageContainer maxWidth={settings?.pages.pageMaxWidth}>
        <CategoryMenuStyle2>
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Rechercher une prestation ou catégorie..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearch("")}
                    edge="end"
                    sx={{ color: "#fff" }}
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
              style: {
                color: "#fff",
              },
            }}
            sx={{
              backgroundColor: "#111",
              flexGrow: 1,
              minWidth: "200px",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#fff",
                },
                "&:hover fieldset": {
                  borderColor: "#aaa",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#fff",
                },
              },
              "& input::placeholder": {
                color: "#ccc",
                opacity: 1,
              },
            }}
          />
          {filteredCategories.map((cat) => (
            <CategoryButton
              key={cat.name}
              onClick={() => scrollToCategory(cat.name)}
            >
              <h1>{cat.name}</h1>
            </CategoryButton>
          ))}
        </CategoryMenuStyle2>

        <ContentSection>
          {filteredCategories.map((cat) => (
            <CategorySection
              key={cat.name}
              ref={(el) => (categoryRefs.current[cat.name] = el)}
            >
              <CategoryTitle>{cat.name}</CategoryTitle>
              {cat.description && <Description>{cat.description}</Description>}

              {cat.services.map((service) => (
                <ServiceCard key={service.id}>
                  <ServiceName>{service.name}</ServiceName>
                  <ServiceInfos>
                    {service.duration && `${service.duration}`}
                    {service.price && (
                      <>
                        {" "}
                        - <b>{service.price}</b>
                      </>
                    )}
                  </ServiceInfos>
                </ServiceCard>
              ))}
            </CategorySection>
          ))}
          <RdvButtonSection>
            <RDVButton />
          </RdvButtonSection>
        </ContentSection>
      </PageContainer>
    </PageWrapper>
  );
};

export default PagePrestations;
